import {
  ArticleDiagnosisApiPath,
  ArticleRewriteProgressEvent,
  GenerateArticleRewriteStreamResult,
  GenerateArticleRewriteStreamVars
} from '@/common/api/params/articleDiagnosis'
import { replacePath } from '@/common/api/rest'
import { LocalStorage, loadStorage } from '@/common/util/storage'

export interface ArticleRewriteStreamHandlers {
  onProgress: (progress: ArticleRewriteProgressEvent) => void
  onResult: (result: GenerateArticleRewriteStreamResult) => void
}

/**
 * 記事AIリライト案生成のSSEストリーミング。
 *
 * 中央fetch層（apiPostFetcher）は response.json() するためストリーミングに使えない。
 * ここでは素の fetch を使うが、認証ヘッダは共通の loadStorage(AUTH_TOKEN) を用いる。
 */
export const streamGenerateArticleRewrite = async (
  vars: GenerateArticleRewriteStreamVars,
  handlers: ArticleRewriteStreamHandlers,
  signal?: AbortSignal
): Promise<void> => {
  const { path, params } = replacePath(
    ArticleDiagnosisApiPath.GENERATE_ARTICLE_REWRITE_STREAM,
    vars
  )

  const response = await fetch(path, {
    method: 'POST',
    signal,
    cache: 'no-store',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${loadStorage(LocalStorage.AUTH_TOKEN) || ''}`
    },
    body: JSON.stringify(params)
  })
  if (!response.ok || !response.body) {
    throw response
  }

  const reader = response.body.getReader()
  const decoder = new TextDecoder()
  let buffer = ''

  while (true) {
    const { done, value } = await reader.read()
    if (done) break
    buffer += decoder.decode(value, { stream: true })
    const lines = buffer.split('\n')
    buffer = ''
    let currentEvent = ''
    for (const line of lines) {
      if (line.startsWith('event: ')) {
        currentEvent = line.slice(7).trim()
      } else if (line.startsWith('data: ')) {
        try {
          const data = JSON.parse(line.slice(6))
          if (currentEvent === 'progress') handlers.onProgress(data as ArticleRewriteProgressEvent)
          else if (currentEvent === 'result')
            handlers.onResult(data as GenerateArticleRewriteStreamResult)
        } catch {
          // 不完全な行は無視
        }
        currentEvent = ''
      } else if (line !== '') {
        buffer += line + '\n'
      }
    }
  }
}
